'use client';

import { ROUTES, useClientConfig, useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps, MouseEvent } from 'react';
import { Link } from '../../../Link/Link';
import { StatusNotification } from '../../../StatusNotification/StatusNotification';

type LegacySitePurchasesNotificationVariant = 'purchases' | 'categories';

type LegacySitePurchasesNotificationProps = Pick<ComponentProps<'div'>, 'className'> & {
  variant?: LegacySitePurchasesNotificationVariant;
};

const MESSAGE_KEYS = {
  purchases: 'legacy_site_notification.message',
  categories: 'legacy_site_notification.category_pages_message',
} as const;

function LegacySitePurchasesNotification({ className, variant = 'purchases', ...props }: LegacySitePurchasesNotificationProps) {
  const t = useTranslations('lotteries.purchase');
  const isMobile = useIsMobile();
  const legacyPurchaseUrl = isMobile ? ROUTES.LEGACY_PURCHASE.MOBILE : ROUTES.LEGACY_PURCHASE.DESKTOP;
  const { legacySiteUrl } = useClientConfig();

  const handleLegacySiteClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('legacy:open', { detail: { href: legacyPurchaseUrl } }));
  };

  return (
    <StatusNotification
      type="info"
      className={className}
      description={t.rich(MESSAGE_KEYS[variant], {
        link: chunks => (
          <Link
            href={`${legacySiteUrl}${legacyPurchaseUrl}`}
            size="explanatory"
            onClick={handleLegacySiteClick}
            aria-label={t('legacy_site_notification.link_aria_label')}
            data-testid="legacy-site-purchases-link"
            data-track-name="Classic"
          >
            {chunks}
          </Link>
        ),
      })}
      {...props}
    />
  );
}

export { LegacySitePurchasesNotification };
